<template>
  <div class="wtf-card">
    <!-- Avatar -->
    <div class="wtf-avatar flex-shrink-0">
      <!-- User's avatar, linking to their profile; verified/admin/current-user flags control its badge/ring styling -->
      <BaseAvatar
        :storage-id="getData.storageId"
        :verified="getData.verified"
        :href="userRoute"
        :isAdmin="getData.isAdmin && isAdmin"
        :rating="getData.rating"
        :isCurrentUser="userdata?.id === getData.userId"
        target="_self"
      />
    </div>

    <!-- Info -->
    <div class="wtf-info min-w-0 flex-1">
      <!-- Row 1: Full name + badge -->
      <div class="wtf-name-row" @click.stop="handleClick" :title="fullName">
        <span class="wtf-name">{{ fullName }}</span>
        <!-- Optional profile badge icon (verified/heart/flame/etc.), only rendered when one is resolved -->
        <img v-if="badgeIconSrc" :src="badgeIconSrc" alt="badge"
          loading="lazy" decoding="async" class="wtf-badge" />
      </div>

      <!-- Row 2: @username + Follow button -->
      <div class="wtf-username-row">
        <!-- Clickable @username, navigates to the profile; empty spacer keeps layout when there's no username -->
        <span v-if="getData.username" class="wtf-username truncate" @click.stop="handleClick" :title="getData.username">
          @{{ getData.username }}
        </span>
        <span v-else class="flex-1" />
        <!-- Follow/unfollow toggle button; hidden when viewing your own card or when not logged in -->
        <ButtonFollow
          v-if="isAuth && data.id !== userdata?.id"
          :loading="followLoading"
          :isFollow="isFollowing"
          @click="handleFollow(getData.userId)"
          class="wtf-follow-btn"
          :class="{ 'wtf-follow-btn--following': isFollowing }"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useStore } from 'vuex';
import ButtonFollow from '@/components/ui-components/NewButtonFollow/index.vue';
import { useFollowNotification } from '@/helpers/notificationHelpers/notificationMessages';
import { makeLegacyDecoration } from '@/helpers/urlDecorationHelper.js';
import { BaseURL } from '@/global/baseUrls.js';

const router = useRouter();
const store = useStore();
const toast = useToast();
const notify = useFollowNotification();

/** The currently logged-in user, from the Vuex store. */
const userdata = computed(() => store.getters.getUser);
/** Whether the logged-in (viewing) user has the admin role. */
const isAdmin = computed(() => userdata.value?.roles?.includes('admin') || false);

/**
 * @prop {Object} data - The suggested-follow user record to render.
 * @prop {Boolean} isAuth - Whether the viewer is authenticated; controls whether the Follow button is shown.
 */
const props = defineProps({
  data: { type: Object, default: null },
  isAuth: { type: Boolean, default: false },
});

/** Normalizes the raw `props.data` user record into the fields this card needs, tolerating a null/partial `data`. */
const getData = computed(() => {
  const user = props.data || {};
  return {
    storageId: user?.avatarFile?.storageId,
    userId: user.id,
    verified: user.verified,
    isAdmin: user.roles?.includes('admin') || false,
    username: user.username,
    rating: user.rating,
    firstName: user.firstName,
    lastName: user.lastName,
    userBadgeIcon: user.userBadgeIcon,
  };
});

/** Display name: "First Last" if both are known, otherwise whichever of first/last/username is available. */
const fullName = computed(() => {
  const { firstName, lastName, username } = getData.value;
  if (firstName && lastName) return `${firstName} ${lastName}`;
  return firstName || lastName || username || '';
});

/** Profile route for this user (by username if known, else by id), with the legacy URL decoration suffix appended. */
const userRoute = computed(() => {
  const route = props.data?.username
    ? `/user/${props.data.username}`
    : `/profile/${props.data?.id}`;
  return route + makeLegacyDecoration();
});

/** Maps known badge keys to their CDN icon URLs. */
const BADGE_ICON_MAP = {
  verified: 'https://cdn.digicollect.com/cdn/launchpad/verified.png',
  heart: 'https://cdn.digicollect.com/cdn/launchpad/heart.png',
  flame: 'https://cdn.digicollect.com/cdn/launchpad/flame.png',
  star: 'https://cdn.digicollect.com/cdn/launchpad/star.png',
  crown: 'https://cdn.digicollect.com/cdn/launchpad/crown.png',
  sparkles: 'https://cdn.digicollect.com/cdn/launchpad/sparkles.png',
};
/**
 * Resolves the badge image to show: a known key maps to a CDN icon in
 * `BADGE_ICON_MAP`, an unknown key is treated as a relative path against
 * `BaseURL`, and no key means no badge.
 */
const badgeIconSrc = computed(() => {
  const k = getData.value?.userBadgeIcon ?? '';
  return BADGE_ICON_MAP[k] || (k ? `${BaseURL}${k}` : '');
});

/** Whether the logged-in user is already following this card's user; null if `following` data isn't loaded yet. */
const isFollowing = computed(() => {
  if (!Array.isArray(userdata.value?.following)) return null;
  return userdata.value.following.includes(getData.value.userId);
});

/** Navigates to this user's profile page (used by the name/username click handlers). */
const handleClick = () => {
  router.push(userRoute.value);
};

/** Whether a follow/unfollow request is currently in flight, used to disable the button and prevent double-submits. */
const followLoading = ref(false);

/** Re-fetches the logged-in user's profile so `userdata` (and its `following` list) reflects the latest server state. */
const refreshProfile = async () => {
  try { await store.dispatch('getMe'); } catch (e) { /* silent */ }
};

// OLD: updated userdata.following only after the API responded, so the
// "Who to Follow" list lagged behind the click.
// const handleFollow = async (id) => {
//   followLoading.value = true;
//   try {
//     if (!isFollowing.value) {
//       const res = await fetch(`/api/users/${id}/follow`, { method: 'POST' });
//       const data = await res.json();
//       if (!data.error) {
//         notify('success', 'followed', fullName.value);
//         userdata.value.following.push(id);
//         refreshProfile();
//       }
//     } else {
//       const res = await fetch(`/api/users/${id}/unfollow`, { method: 'POST' });
//       const data = await res.json();
//       if (!data.error) {
//         notify('success', 'unfollowed', fullName.value);
//         userdata.value.following = userdata.value.following.filter(uid => uid !== id);
//         refreshProfile();
//       }
//     }
//   } catch (e) {
//     toast.add({ severity: 'error', summary: 'Error', detail: e.message, life: 5000 });
//   } finally {
//     followLoading.value = false;
//   }
// };

// Optimistic follow/unfollow: update userdata.following BEFORE the API call so the
// "Who to Follow" list reacts instantly; roll back if the request fails.
/** Adds `id` to the logged-in user's local `following` list, if not already present. */
const addFollowing = (id) => {
  if (!userdata.value) return;
  if (!Array.isArray(userdata.value.following)) userdata.value.following = [];
  if (!userdata.value.following.includes(id)) userdata.value.following.push(id);
};
/** Removes `id` from the logged-in user's local `following` list. */
const removeFollowing = (id) => {
  if (!Array.isArray(userdata.value?.following)) return;
  userdata.value.following = userdata.value.following.filter(uid => uid !== id);
};

/**
 * Follows or unfollows the given user, optimistically updating the local
 * `following` list first so the UI (and this "Who to Follow" list) reacts
 * instantly, then calling the API; rolls the optimistic update back and
 * shows an error toast if the request fails or errors.
 * @param {string} id - User id to follow/unfollow
 */
const handleFollow = async (id) => {
  if (followLoading.value) return;
  followLoading.value = true;
  const wasFollowing = !!isFollowing.value;
  // Optimistic update — list re-filters immediately
  if (!wasFollowing) addFollowing(id);
  else removeFollowing(id);
  try {
    const endpoint = wasFollowing ? 'unfollow' : 'follow';
    const res = await fetch(`/api/users/${id}/${endpoint}`, { method: 'POST' });
    const data = await res.json();
    if (!data.error && res.ok) {
      notify('success', wasFollowing ? 'unfollowed' : 'followed', fullName.value);
      refreshProfile();
    } else {
      // Roll back the optimistic update on API failure
      if (!wasFollowing) removeFollowing(id);
      else addFollowing(id);
      toast.add({ severity: 'error', summary: 'Error', detail: data.message || `Could not ${endpoint} user`, life: 5000 });
    }
  } catch (e) {
    // Roll back the optimistic update on network error
    if (!wasFollowing) removeFollowing(id);
    else addFollowing(id);
    toast.add({ severity: 'error', summary: 'Error', detail: e.message, life: 5000 });
  } finally {
    followLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
.wtf-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-text-muted);
}

.wtf-avatar {
  display: flex;
  align-items: center;
}

.wtf-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.wtf-name-row {
  display: block;
  margin-bottom: 2px;
  cursor: pointer;
  min-width: 0;
 text-align: left;
  &:hover .wtf-name { text-decoration: underline; }
}

.wtf-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-quick-link-icon-bg);
  word-break: break-word;
  text-align: left;
  display: inline;
}

.wtf-badge {
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
  margin-bottom: 2px;
}

.wtf-username-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  width: 100%;
}

.wtf-username {
  font-size: 0.75rem;
  color: var(--color-text-02);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  text-align: left;

  &:hover { text-decoration: underline; }
}

.wtf-follow-btn {
  flex-shrink: 0;
  align-self: center;
  font-size: 0.8rem;
  font-family: "Poppins", sans-serif;
  padding: 3px 8px;
  border-radius: 5.8px;
  border: 1px solid var(--color-button-border);
  background-color: var(--color-accent-primary);
  color: var(--color-text-black-white);
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  box-shadow: var(--button-shadow, 0 1px 2px rgba(0, 0, 0, 0.08));
  font-weight: 600;
  width: auto;
  max-width: 80px;
  min-width: 56px;
  white-space: nowrap;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 22px;

  &--following {
    background-color: transparent;
    border: 1px solid var(--color-brand-secondary);
    color: var(--color-brand-secondary);
  }
}
</style>
